import { useFormContext } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { lt, useToggle } from '@mews/framework';
import { Select } from '@mews-commander/ui';
import {
    Stack,
    Table,
    TableActiveColumnFilters,
    ActionToolbar,
    TableProvider,
    useRowSelection,
    useTableState,
} from '@optimus-web/core';
import {
    PortfolioEnterpriseRelationshipFilters,
    PortfolioEnterpriseRelationshipResult,
    PortfolioStringFilter,
} from '@mews-commander/data-access';
import {
    AdjustmentType,
    getCurrentServiceFieldName,
    getServicesPropertiesFieldName,
    PricingFieldNames,
    ServicesFieldNames,
} from '../../../../forms/schemas/configureProductFormSchema';
import { ServicesPricingTables, useSegmentedPropertiesTable } from '../../../../../hooks/useSegmentedPropertiesTable';
import { PriceBulkActions } from '../../../common/PriceBulkActions';
import { useProperties } from '../../../../../hooks/useProperties';
import { useGetDataWithDefaultCurrency } from '../../../../../hooks/useGetDataWithDefaultCurrency';
import { getFixedPricingColumns } from './getFixedPricingColumns';

export const FixedPricing = ({ list, currentService }: ServicesPricingTables) => {
    const {
        watch,
        setValue,
        formState: { isSubmitting, isValid },
    } = useFormContext();

    const currentServiceFieldName = `${getCurrentServiceFieldName(currentService)}.${
        AdjustmentType.FixedPriceAdjustments
    }`;
    const currentPropertiesIds = Object.keys(watch(currentServiceFieldName) || '');

    const { selectedOptions, toggleSelection } = useSegmentedPropertiesTable(list, currentServiceFieldName);

    const [basePriceBulkValue, setBasePriceBulkValue] = useState<number | undefined>();
    const [isBulkValueApplied, setIsBulkValueApplied] = useToggle();
    const [tableState, setTableState] = useTableState<
        PortfolioEnterpriseRelationshipResult & { defaultCurrencyCode: string },
        PortfolioEnterpriseRelationshipFilters & { defaultCurrencyCode: Array<PortfolioStringFilter> }
    >({
        pagination: {
            show: false,
        },
    });

    const { data, isFetching } = useGetDataWithDefaultCurrency(currentPropertiesIds);
    const { filteredData, selectedCountries, selectedCurrencies } = useProperties({
        data,
        tableState,
    });

    useEffect(() => {
        if (!isSubmitting || isValid) return;

        // Form submission failed due to validation errors, reset filters to show all inputs in the tables.
        setTableState((state) => ({
            ...state,
            filters: {
                countryName: [],
                defaultCurrencyCode: [],
            },
        }));
    }, [isSubmitting, isValid, setTableState]);

    const { rowSelection, setRowSelection, selectedRowsIds, hasRowsSelected } = useRowSelection();

    const columns = useMemo(
        () => getFixedPricingColumns(selectedCountries, selectedCurrencies, currentService),
        // Re-render when a bulk value is applied to update the values of the input fields.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedCountries, selectedCurrencies, isBulkValueApplied, currentService]
    );

    const handleOnClick = useCallback(() => {
        selectedRowsIds.forEach((id) => {
            const fieldName = `${getServicesPropertiesFieldName(
                currentService,
                AdjustmentType.FixedPriceAdjustments,
                id
            )}.${ServicesFieldNames.Value}`;
            setValue(fieldName, basePriceBulkValue, { shouldDirty: true, shouldValidate: true });
        });

        setIsBulkValueApplied();
    }, [selectedRowsIds, setIsBulkValueApplied, setValue, basePriceBulkValue, currentService]);

    useEffect(() => {
        if (data.length > 0) {
            data.forEach(({ id, defaultCurrencyCode }) =>
                setValue(
                    `${getServicesPropertiesFieldName(currentService, AdjustmentType.FixedPriceAdjustments, id)}.${
                        PricingFieldNames.CurrencyCode
                    }`,
                    defaultCurrencyCode
                )
            );
        }
    }, [data, currentService, setValue]);

    return (
        <>
            <Select
                label={lt('ConfigureProductDropdownProperties')}
                multiselect
                options={list}
                selected={selectedOptions?.map(({ value }) => value)}
                onChange={toggleSelection}
            />

            {currentPropertiesIds.length > 0 && (
                <Stack vertical spacing="300">
                    <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                        <TableActiveColumnFilters />

                        <ActionToolbar
                            disabled={!hasRowsSelected}
                            leftSide={lt('SelectedItems', { count: selectedRowsIds.length })}
                            rightSide={
                                <PriceBulkActions
                                    basePriceBulkValue={basePriceBulkValue}
                                    handleOnClick={handleOnClick}
                                    hasRowsSelected={hasRowsSelected}
                                    setBasePriceBulkValue={setBasePriceBulkValue}
                                />
                            }
                        />

                        <Table
                            uniqueRowId="id"
                            fixedFirstColumn
                            data={filteredData}
                            amountOfRows={data.length}
                            loading={isFetching}
                            rowSelection={{
                                rowSelection,
                                onRowSelectionChange: setRowSelection,
                            }}
                        />
                    </TableProvider>
                </Stack>
            )}
        </>
    );
};
